<script lang="ts">
  import { onMount } from "svelte";
  import { ping } from "./lib/workerClient";
  import { authState } from "./lib/auth/store.svelte";
  import {
    handleCallback,
    startSignIn,
    signOut,
    loadInitialAuthState,
  } from "./lib/auth/oauth";
  import { loadInitialPushState, unsubscribe } from "./lib/push/subscribe";
  import { trackerState } from "./lib/tracker/store.svelte";
  import { gmailState } from "./lib/gmail/store.svelte";
  import { loadInbox, markUnread, archiveThread } from "./lib/gmail/sync";
  import PushBanner from "./lib/components/PushBanner.svelte";
  import TrackerDashboard from "./lib/components/TrackerDashboard.svelte";
  import TrackerDetail from "./lib/components/TrackerDetail.svelte";
  import InboxView from "./lib/components/InboxView.svelte";
  import ThreadView from "./lib/components/ThreadView.svelte";
  import ComposeView from "./lib/components/ComposeView.svelte";
  import Snackbar from "./lib/components/Snackbar.svelte";
  import SearchBar from "./lib/components/SearchBar.svelte";
  import ComposeFab from "./lib/components/ComposeFab.svelte";
  import SnoozeSheet from "./lib/components/SnoozeSheet.svelte";
  import LabelSheet from "./lib/components/LabelSheet.svelte";
  import PullIndicator from "./lib/components/PullIndicator.svelte";
  import Icon from "./lib/components/shared/Icon.svelte";
  import TabBar, { type Tab } from "./lib/components/TabBar.svelte";
  import SettingsView from "./lib/components/SettingsView.svelte";
  import TriageView from "./lib/components/TriageView.svelte";
  import { initTheme } from "./lib/theme.svelte";
  import { loadSettings } from "./lib/settings.svelte";
  import CrmView from "./lib/components/CrmView.svelte";
  import { crmState } from "./lib/crm/store.svelte";
  import { initCrmSession } from "./lib/crm/session.svelte";
  import { initWriteQueue } from "./lib/crm/write.svelte";
  import { loadLabels } from "./lib/gmail/labels.svelte";
  import { loadThreadFlags, flagsState } from "./lib/gmail/threadFlags.svelte";
  import { loadContacts } from "./lib/gmail/contacts.svelte";
  import { openSnooze } from "./lib/components/snoozeStore.svelte";
  import { swipeBack } from "./lib/gestures/swipeBack";
  import { pullDown } from "./lib/gestures/pullDown";
  import { haptic } from "./lib/gestures/haptic";
  import {
    initHistoryBaseline,
    pushNav,
    replaceNav,
    popNav,
    onPopNav,
  } from "./lib/gestures/historyNav";

  const workerUrl = import.meta.env.VITE_WORKER_URL;
  const isDev = import.meta.env.DEV;

  // === État UI ============================================================
  let workerReachable = $state<boolean | null>(null);
  let bootDone = $state(false);
  let callbackError = $state<string | null>(null);
  // Onglet racine actif. Les cinq vivent au même étage de la pile history.
  let activeTab = $state<Tab>("inbox");
  // Phase C — searchbar visible (révélée par pull-down sur l'inbox)
  let searchVisible = $state(false);
  // Phase D — double-tap topbar = scroll to top
  let lastTopbarTap = 0;
  // Bind l'élément du PullIndicator → passé à l'action pullDown pour
  // qu'elle puisse switcher data-pull-mode pendant le drag.
  let pullIndicatorEl = $state<HTMLDivElement | undefined>();

  const isCallback = window.location.pathname === "/oauth-callback";

  type View = "loading" | "callback-error" | "signin" | "app";
  const view = $derived.by<View>(() => {
    if (!bootDone) return "loading";
    if (callbackError) return "callback-error";
    if (authState.signedIn) return "app";
    return "signin";
  });

  // Une sous-vue = un écran empilé PAR-DESSUS un onglet, donc le seul cas
  // qui mérite un bouton retour. Les onglets, eux, se rejoignent par la
  // tab bar : leur donner un « retour » serait mentir sur la pile.
  const inSubView = $derived(
    !!gmailState.selectedThreadId ||
      (activeTab === "crm" && !!crmState.selectedUid),
  );

  /** Compose ouvert = feuille plein écran : la tab bar s'efface. */
  const composeOpen = $derived(!!gmailState.composeDraft);

  const unreadCount = $derived(gmailState.rows.filter((r) => r.unread).length);
  const triageCount = $derived(
    Object.values(flagsState.byId).filter((f) => f.later === true).length,
  );

  // === Navigation history sync ===========================================
  // Source de vérité = la pile history. Les sub-views poussent une entrée à
  // l'entrée, popNav() sort (= history.back() = déclenche popstate). Le
  // popstate handler reset l'état UI. Permet au swipe-back ET au bouton
  // back hardware de partager le même mécanisme.
  //
  // Le $effect surveille le couple (selectedThreadId, activeTab) et
  // synchronise la pile. `suppressNextNav` évite la boucle quand c'est le
  // popstate handler qui vient de muter l'état.
  let mounted = false;
  let suppressNextNav = false;
  let lastViewSig: string | null = null;

  const viewSig = $derived(
    gmailState.selectedThreadId
      ? `thread:${gmailState.selectedThreadId}`
      : activeTab === "crm" && crmState.selectedUid
        ? `crm:${crmState.selectedUid}`
        : `tab:${activeTab}`,
  );

  $effect(() => {
    const sig = viewSig;
    if (!mounted) {
      mounted = true;
      lastViewSig = sig;
      return;
    }
    if (sig === lastViewSig) return;
    if (suppressNextNav) {
      suppressNextNav = false;
      lastViewSig = sig;
      return;
    }
    if (sig.startsWith("tab:")) {
      // Un onglet REMPLACE l'entrée courante : les cinq racines partagent un
      // seul étage. Sinon « retour » ferait défiler l'historique des onglets
      // au lieu de sortir de l'app, et l'utilisateur ne saurait jamais
      // combien de retours il lui reste à faire.
      replaceNav({ view: "tab", tab: sig.slice(4) });
    } else if (sig.startsWith("thread:")) {
      pushNav({ view: "thread", threadId: sig.slice(7) });
    } else if (sig.startsWith("crm:")) {
      const nav = { view: "crm" as const, companyUid: sig.slice(4) };
      // Passer d'une fiche à la suivante REMPLACE l'entrée au lieu d'empiler :
      // « retour » ramène toujours à la liste, quel que soit le nombre de
      // fiches enchaînées. Sinon, arrêter après vingt sociétés demanderait
      // vingt retours.
      if (lastViewSig?.startsWith("crm:")) replaceNav(nav);
      else pushNav(nav);
    }
    lastViewSig = sig;
  });

  // === Boot ===============================================================
  onMount(async () => {
    ping().then((ok) => (workerReachable = ok));

    if (isCallback) {
      try {
        await handleCallback();
      } catch (e: unknown) {
        callbackError = e instanceof Error ? e.message : String(e);
      }
    } else {
      await loadInitialAuthState();
    }

    await loadInitialPushState();

    // Miroir CRM : session Supabase (indépendante du Google du mail) et file
    // d'écritures laissée par la session précédente.
    void initCrmSession();
    void initWriteQueue();

    // Le thème choisi s'applique avant tout rendu d'écran.
    initTheme();
    // Gestes de balayage et haptique — lus par la Boîte et par `haptic()`.
    void loadSettings();

    // Établit la baseline avant tout deep-link handling — l'entrée
    // courante de la pile devient l'onglet Boîte.
    initHistoryBaseline("inbox");

    // Charge les labels en arrière-plan (n'attend pas — UI montre Inbox
    // par défaut, les autres tabs apparaissent quand le fetch revient).
    if (authState.signedIn) {
      void loadLabels();
    }
    // Hydrate les flags later/snoozed locaux (factice v1, IDB)
    void loadThreadFlags();
    // Hydrate l'index des contacts pour l'autocomplete `from:`/`to:`/`cc:`
    void loadContacts();

    const params = new URL(window.location.href).searchParams;
    const pixelParam = params.get("pixel");
    const viewParam = params.get("view");
    const idParam = params.get("id");
    if (pixelParam && authState.signedIn) {
      // Tap sur notif push tracker (cold-start) → ouvre direct la modale détail
      activeTab = "tracker";
      trackerState.detailPixelId = pixelParam;
      window.history.replaceState({}, document.title, "/");
    } else if (viewParam === "thread" && idParam && authState.signedIn) {
      // Tap sur notif push gmail_wakeup enrichie (cold-start) → ouvre
      // directement le thread du mail. ThreadView lance le loadThread
      // dans son $effect dès qu'il voit `selectedThreadId`. La pile
      // `back` revient sur la Boîte.
      activeTab = "inbox";
      gmailState.selectedThreadId = idParam;
      gmailState.currentThread = null;
      window.history.replaceState({}, document.title, "/");
    } else if (viewParam === "inbox" && authState.signedIn) {
      // Tap sur notif push gmail_wakeup non-enrichie (fallback) → atterrit
      // sur la Boîte. InboxView appelle loadInbox au mount → sync immédiat.
      activeTab = "inbox";
      gmailState.selectedThreadId = null;
      gmailState.currentThread = null;
      window.history.replaceState({}, document.title, "/");
    }

    // Si on a atterri sur un sub-view via deep-link, push une entrée
    // history pour que swipe-back / back hardware ferme proprement.
    // Set explicit lastViewSig pour que le $effect ne re-push pas au mount.
    if (gmailState.selectedThreadId) {
      pushNav({ view: "thread", threadId: gmailState.selectedThreadId });
      lastViewSig = `thread:${gmailState.selectedThreadId}`;
    } else {
      // Un deep-link tracker n'empile rien : c'est un onglet racine, il
      // remplace simplement la baseline.
      if (activeTab !== "inbox") replaceNav({ view: "tab", tab: activeTab });
      lastViewSig = `tab:${activeTab}`;
    }
    mounted = true;

    // Branche le popstate handler — un back déclenché soit par notre
    // popNav() (gestes/UI), soit par le bouton hardware/trackpad.
    onPopNav((state) => {
      suppressNextNav = true;
      if (!state || state.view === "tab") {
        gmailState.selectedThreadId = null;
        gmailState.currentThread = null;
        crmState.selectedUid = null;
        if (state?.tab) activeTab = state.tab as Tab;
      } else if (state.view === "thread" && state.threadId) {
        gmailState.selectedThreadId = state.threadId;
        crmState.selectedUid = null;
      } else if (state.view === "crm") {
        activeTab = "crm";
        gmailState.selectedThreadId = null;
        crmState.selectedUid = state.companyUid ?? null;
      }
    });

    // Service worker postMessage handler (PWA déjà foreground quand
    // l'user tap la notif). Le tracker dashboard a son propre handler
    // pour `open-pixel` (lib/tracker/sync.ts) ; ici on gère :
    //   - `open-thread` : tap sur notif gmail enrichie (le SW a un threadId)
    //   - `open-inbox`  : tap sur notif gmail générique (fallback enrichissement)
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", (e) => {
        const data = e.data as { type?: string; threadId?: string };
        if (data?.type === "open-thread" && data.threadId) {
          activeTab = "inbox";
          gmailState.selectedThreadId = data.threadId;
          gmailState.currentThread = null;
        } else if (data?.type === "open-inbox") {
          activeTab = "inbox";
          gmailState.selectedThreadId = null;
          gmailState.currentThread = null;
          // Force un refresh — l'utilisateur vient juste de recevoir
          // une notif "Nouveau mail", il s'attend à ce que la liste
          // soit à jour au moment où il bascule sur l'inbox.
          void loadInbox();
        }
      });
    }

    bootDone = true;
  });

  // === Actions =============================================================
  function goBack() {
    // Délègue à la pile history : popNav → history.back() → popstate
    // handler reset l'état UI. Centralise la logique : que l'user clique
    // le bouton, swipe-back, ou utilise un back hardware, on passe par
    // le même chemin.
    if (inSubView) popNav();
  }

  // Phase D — double-tap topbar = scroll to top de la liste actuelle.
  // Pattern iOS standard (Safari, Twitter, etc.).
  function onTopbarTap() {
    const now = Date.now();
    if (now - lastTopbarTap < 350) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      haptic("light");
      lastTopbarTap = 0;
    } else {
      lastTopbarTap = now;
    }
  }

  /**
   * Changer d'onglet ferme toute sous-vue ouverte : les onglets sont des
   * racines, on n'en emporte pas un fil ou une fiche avec soi.
   */
  function selectTab(tab: Tab) {
    gmailState.selectedThreadId = null;
    gmailState.currentThread = null;
    crmState.selectedUid = null;
    activeTab = tab;
  }

  // Ouvrir/fermer une fiche passe par la pile history, comme les threads :
  // le geste swipe-back et le bouton retour partagent ainsi le même chemin.
  function openCompany(uid: string) {
    crmState.selectedUid = uid;
  }

  function closeCompany() {
    popNav();
  }


  async function onSignOut() {
    if (!confirm("Se déconnecter ? Les notifications seront aussi désactivées.")) {
      return;
    }
    try {
      await unsubscribe();
    } catch (e) {
      console.warn("unsubscribe at sign-out failed:", e);
    }
    await signOut();
    activeTab = "inbox";
  }

  function onRetryAfterError() {
    callbackError = null;
    window.location.assign("/");
  }
</script>

<div class="app" class:dev={isDev}>
  <!-- La topbar n'existe plus que POUR REVENIR, et seulement là où l'écran
       n'a pas déjà son propre en-tête : les onglets et le Fil portent le
       leur, empiler une barre globale par-dessus doublerait le titre. Il ne
       reste que la fiche société. -->
  {#if view === "app" && inSubView && !gmailState.selectedThreadId}
    <!-- svelte-ignore a11y_click_events_have_key_events -->
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <header class="topbar" onclick={onTopbarTap}>
      <div class="left">
        <button class="icon-btn" onclick={goBack} aria-label="Retour">
          <Icon name="chevron-left" size={20} />
        </button>
      </div>

      {#if isDev}
        <div class="dev-pill" aria-label="Environnement de développement">DEV</div>
      {/if}

      <div class="right">
        {#if gmailState.selectedThreadId}
          <button
            class="icon-btn"
            onclick={() => gmailState.selectedThreadId && openSnooze(gmailState.selectedThreadId)}
            aria-label="Reporter"
            title="Reporter"
          >
            <Icon name="clock" size={18} />
          </button>
          <button
            class="icon-btn"
            onclick={() => {
              const id = gmailState.selectedThreadId;
              if (id) {
                void markUnread(id);
                popNav();
              }
            }}
            aria-label="Marquer non lu et retour"
            title="Marquer non lu"
          >
            <Icon name="mail" size={18} />
          </button>
          <button
            class="icon-btn"
            onclick={() => {
              const id = gmailState.selectedThreadId;
              if (id) {
                void archiveThread(id);
                popNav();
              }
            }}
            aria-label="Archiver et retour"
            title="Archiver"
          >
            <Icon name="archive" size={18} />
          </button>
        {/if}
      </div>
    </header>
  {/if}

  <main class:tabbed={view === "app" && !composeOpen}>
    {#if view === "loading"}
      <p class="muted center">Chargement…</p>
    {:else if view === "callback-error"}
      <section class="card-msg">
        <h2>Erreur de connexion</h2>
        <p class="err">{callbackError}</p>
        <button class="btn-primary" onclick={onRetryAfterError}>Recommencer</button>
      </section>
    {:else if view === "signin"}
      <section class="signin-card">
        <h2>Mailfox</h2>
        <p class="muted">
          Connecte-toi avec ton compte Google pour activer le tracker push et lire ton inbox.
        </p>
        <button class="btn-primary big" onclick={startSignIn}>
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Sign in with Google
        </button>
      </section>
    {:else if view === "app"}
      <!-- Wrapper swipe-back : applique le translateX pendant le geste,
           inactif quand on est sur l'inbox racine (rien à faire). -->
      <div class="view-stack" use:swipeBack={{ onBack: goBack, enabled: inSubView }}>
        {#if gmailState.selectedThreadId}
          <ThreadView />
        {:else if activeTab === "crm"}
          <CrmView
            selectedUid={crmState.selectedUid}
            onOpen={openCompany}
            onClose={closeCompany}
          />
        {:else if activeTab === "tracker"}
          <PushBanner />
          <TrackerDashboard />
        {:else if activeTab === "triage"}
          <TriageView onQuit={() => selectTab("inbox")} />
        {:else if activeTab === "settings"}
          <SettingsView {workerUrl} {workerReachable} onSignOut={onSignOut} />
        {:else}
          <SearchBar
            visible={searchVisible}
            onClose={() => (searchVisible = false)}
          />
          <div
            class="pull-wrap"
            use:pullDown={{
              onRefresh: () => loadInbox(),
              onReveal: () => (searchVisible = true),
              enabled: !searchVisible && !gmailState.loading,
              indicator: pullIndicatorEl,
            }}
          >
            <PullIndicator bind:el={pullIndicatorEl} />
            <InboxView onSearch={() => (searchVisible = true)} />
          </div>
        {/if}
      </div>
    {/if}
  </main>

  <!-- FAB compose, sur la Boîte uniquement. -->
  {#if view === "app" && !inSubView && activeTab === "inbox"}
    <ComposeFab />
  {/if}

  <!-- Tab bar : la navigation racine. Masquée pendant Compose, qui est une
       feuille plein écran. -->
  {#if view === "app" && !composeOpen}
    <TabBar
      active={activeTab}
      onSelect={selectTab}
      unread={unreadCount}
      triage={triageCount}
    />
  {/if}

  {#if view === "app"}
    <TrackerDetail />
    <ComposeView />
  {/if}

  <SnoozeSheet />
  <LabelSheet />
  <Snackbar />
</div>

<style>
  .app {
    font-family: var(--font-sans);
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    background: var(--bg);
    color: var(--fg);
  }
  .app.dev {
    /* Indicateur dev discret : barre clay à gauche */
    border-left: 3px solid var(--accent);
  }

  /* === Top bar — editorial-minimal ===================================== */
  .topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: calc(env(safe-area-inset-top) + 10px) 16px 10px;
    position: sticky;
    top: 0;
    background: color-mix(in srgb, var(--bg) 80%, transparent);
    backdrop-filter: saturate(180%) blur(20px);
    -webkit-backdrop-filter: saturate(180%) blur(20px);
    z-index: 20;
    border-bottom: 0.5px solid var(--border-subtle);
  }
  .left, .right {
    display: flex;
    align-items: center;
    gap: 2px;
  }
  .icon-btn {
    background: transparent;
    border: none;
    cursor: pointer;
    color: var(--fg-muted);
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: grid;
    place-items: center;
    transition: background 0.1s;
    -webkit-tap-highlight-color: transparent;
  }
  .icon-btn:hover { background: color-mix(in srgb, var(--fg) 6%, transparent); }
  .icon-btn:active { background: color-mix(in srgb, var(--fg) 12%, transparent); }

  .dev-pill {
    font-family: var(--font-mono);
    font-size: 9.5px;
    font-weight: 600;
    letter-spacing: 0.1em;
    padding: 3px 7px;
    border-radius: 4px;
    background: color-mix(in srgb, var(--accent) 14%, transparent);
    color: var(--accent);
    user-select: none;
    text-transform: uppercase;
  }

  /* === Dropdown menu ====================================================== */
  main {
    flex: 1;
    max-width: 640px;
    margin: 0 auto;
    width: 100%;
    /* Aucun padding horizontal : chaque écran gère ses marges, sinon un
       en-tête collant pleine largeur devient impossible. */
    padding: 0;
  }
  /* Place réservée sous le contenu pour que la tab bar fixe ne recouvre
     jamais la dernière ligne d'une liste. */
  main.tabbed {
    padding-bottom: calc(env(safe-area-inset-bottom) + 60px);
  }

  /* Wrapper du contenu sub-view, recevant le translateX pendant un swipe-back.
     `will-change: transform` pré-allocate une compositor layer → animation
     sur 60fps même pendant un drag actif sans repaint coûteux. */
  .view-stack {
    will-change: transform;
  }

  /* Wrapper du pull-down : position relative pour ancrer le PullIndicator
     en absolute au-dessus, will-change pour le drag fluide. */
  .pull-wrap {
    position: relative;
    will-change: transform;
  }

  /* === Sign-in / loading / error ========================================= */
  h2 {
    font-size: 1rem;
    font-weight: 600;
    margin: 1.5rem 0 0.5rem;
    color: var(--fg-muted);
  }
  .center {
    text-align: center;
    padding: 3rem 1rem;
    font-family: var(--font-mono);
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--fg-subtle);
  }
  .signin-card {
    text-align: center;
    padding: 3rem 1.5rem;
    /* `main` ne pousse plus rien horizontalement — la carte tient ses
       propres marges. */
    border: 0.5px solid var(--border);
    border-radius: 14px;
    background: var(--bg-elev);
    margin: 4rem 12px 2rem;
    max-width: 420px;
  }
  .signin-card h2 {
    font-family: var(--font-serif);
    font-size: 31px;
    line-height: 1;
    letter-spacing: -0.015em;
    font-style: italic;
    font-weight: 400;
    letter-spacing: -0.01em;
    line-height: 1.1;
    color: var(--fg);
    margin: 0 0 14px;
  }
  .signin-card p {
    font-size: 13.5px;
    line-height: 1.5;
    color: var(--fg-muted);
  }
  .card-msg {
    padding: 2rem;
    border: 0.5px solid var(--border);
    border-radius: 14px;
    background: var(--bg-elev);
    margin: 2rem 12px;
  }
  .btn-primary {
    background: var(--fg);
    color: var(--bg);
    border: none;
    border-radius: 999px;
    padding: 9px 18px;
    font: inherit;
    font-family: var(--font-sans);
    font-weight: 600;
    font-size: 13px;
    letter-spacing: -0.01em;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-top: 1.2rem;
    -webkit-tap-highlight-color: transparent;
  }
  .btn-primary.big {
    padding: 11px 22px;
    font-size: 14.5px;
  }

  .muted { color: var(--fg-subtle); }
  .err   { color: var(--accent); }
</style>
